/* oxlint-disable anti-slop/no-runtime-typeof */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import SiteRouteHelmet from '../components/SiteRouteHelmet';
import PrerenderReady from '../components/PrerenderReady';
import { Spinner } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useUser from '../hooks/useUser';
import { pushEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import useCheckoutRedirect from '../hooks/useCheckoutRedirect';
import useABTest from '../hooks/useABTest';
import TrustStrip from '../components/TrustStrip';
import { navigateToCheckoutOverlay } from '../utils/checkoutOverlayNavigation';
import { ACTIVE_EXPERIMENTS } from '../utils/experiments';
import { isPreviewExperimentOverrideHost } from '../utils/experimentOverrideHosts';
import PricingPageExperiment from './PricingPageExperiment';
import { buildEducationPricingPlans } from './pricingEducationPlans';
import {
  BUSINESS_PRICING_PLAN as BUSINESS_PLAN,
  EDUCATION_PRICING_PLANS as EDUCATION_PLANS,
  PUBLIC_PRICING_TIERS as PRICING_TIERS,
  formatPublicPrice as formatPrice,
  applyCheckoutCatalogToPricingTiers,
  checkoutCatalogSupportsBillingPeriod,
} from '../site/publicPricingSource';
import './PricingPage.css';

const DIRECT_CHECKOUT_BUTTON_FEEDBACK_MS = 350;
const PRICING_DESIGN_VARIANTS = ['control', 'test'];

function waitForButtonFeedbackPaint() {
  return new Promise((resolve) => {
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(resolve);
      return;
    }
    setTimeout(resolve, 0);
  });
}

async function waitForDirectCheckoutButtonFeedback() {
  await new Promise((resolve) => setTimeout(resolve, DIRECT_CHECKOUT_BUTTON_FEEDBACK_MS));
}

function PricingButtonLoadingLabel() {
  return (
    <>
      <Spinner
        animation="border"
        size="sm"
        className="pricing-page-cta-spinner"
        aria-hidden="true"
        data-testid="pricing-cta-spinner"
      />
      <span>Loading...</span>
    </>
  );
}

function BillingPeriodToggle({ billingPeriod, onChange }) {
  return (
    <div className="pricing-billing-toggle" role="group" aria-label="Billing period">
      <button
        type="button"
        aria-pressed={billingPeriod === 'monthly'}
        onClick={() => onChange('monthly')}
      >
        Monthly
      </button>
      <button
        type="button"
        aria-pressed={billingPeriod === 'annual'}
        onClick={() => onChange('annual')}
      >
        <span>Yearly</span>
        <small>2 months free</small>
      </button>
    </div>
  );
}

export function PricingFeatureItem({ activeFeature, feature, index, onToggleFeature, planId }) {
  const featureKey = `${planId}-${index}`;
  const detailId = `pricing-feature-detail-${planId}-${index}`;
  const hasDetail = Boolean(feature.detail);
  const isExpanded = activeFeature === featureKey;
  const featureContent = (
    <>
      <svg
        className="pricing-page-check"
        width="18"
        height="18"
        viewBox="0 0 20 20"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M16.6667 5L7.50004 14.1667L3.33337 10"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span className="pricing-feature-label">{feature.label}</span>
    </>
  );

  return (
    <li className={`pricing-page-feature ${isExpanded ? 'is-active' : ''}`}>
      {hasDetail ? (
        <button
          type="button"
          className="pricing-feature-chip"
          onClick={() => onToggleFeature(featureKey)}
          aria-expanded={isExpanded}
          aria-controls={detailId}
        >
          {featureContent}
        </button>
      ) : (
        <div className="pricing-feature-chip no-hover">{featureContent}</div>
      )}
      {hasDetail && (
        <div id={detailId} className="pricing-feature-detail" hidden={!isExpanded}>
          <span>{feature.detail}</span>
          {feature.link && (
            <Link className="pricing-feature-detail-link" to={feature.link.to}>
              {feature.link.text}
            </Link>
          )}
        </div>
      )}
    </li>
  );
}

function PricingMobileFeatureDisclosure({ features }) {
  const hiddenFeatures = features.slice(3);

  if (hiddenFeatures.length === 0) {
    return null;
  }

  return (
    <details className="pricing-page-mobile-features">
      <summary>Show {hiddenFeatures.length} more features</summary>
      <ul className="pricing-page-mobile-feature-list">
        {hiddenFeatures.map((feature, index) => (
          <li className="pricing-page-mobile-feature" key={`${feature.label}-${index}`}>
            <svg
              className="pricing-page-check"
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M16.6667 5L7.50004 14.1667L3.33337 10"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              <span className="pricing-page-mobile-feature-label">{feature.label}</span>
              {feature.detail && (
                <span className="pricing-page-mobile-feature-detail">{feature.detail}</span>
              )}
              {feature.link && (
                <Link className="pricing-feature-detail-link" to={feature.link.to}>
                  {feature.link.text}
                </Link>
              )}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

export default function PricingPage() {
  const { isAuthenticated, user } = useUser();
  const navigate = useNavigate();
  const checkout = useCheckoutRedirect('pricing_page');

  return (
    <PricingPageContent
      checkout={checkout}
      currentUser={user}
      isAuthenticated={isAuthenticated}
      navigate={navigate}
      trackEvent={pushEvent}
    />
  );
}

export function PricingPageContent({
  checkout,
  currentUser,
  isAuthenticated,
  navigate,
  trackEvent,
}) {
  const location = useLocation();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedBillingPeriod = queryParams.get('billing') === 'annual' ? 'annual' : 'monthly';
  const [activeFeature, setActiveFeature] = useState(null);
  const [pendingPlanId, setPendingPlanId] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState(requestedBillingPeriod);

  const { startCheckout, catalog, catalogLoading, catalogError } = checkout;

  // Light is always visible on the pricing page. It's a real product, not an A/B-gated teaser.
  const coreTiers = useMemo(
    () =>
      PRICING_TIERS.filter((tier) => ['light', 'starter', 'pro', 'all-access'].includes(tier.id)),
    [],
  );
  const annualBillingAvailable = useMemo(
    () => Boolean(catalog && checkoutCatalogSupportsBillingPeriod(coreTiers, catalog, 'annual')),
    [catalog, coreTiers],
  );
  const effectiveBillingPeriod =
    billingPeriod === 'annual' && annualBillingAvailable ? 'annual' : 'monthly';
  const pricingCatalogProjection = useMemo(() => {
    if (!catalog) return { plans: coreTiers, error: null };
    try {
      return {
        plans: applyCheckoutCatalogToPricingTiers(coreTiers, catalog, effectiveBillingPeriod),
        error: null,
      };
    } catch (error) {
      return { plans: [], error };
    }
  }, [catalog, coreTiers, effectiveBillingPeriod]);
  const CORE_PLANS = pricingCatalogProjection.plans;
  // Bootcamp prices, payment plans and facts come only from the catalog. A
  // Bootcamp catalog problem hides the Bootcamp buy buttons but must not take
  // the subscription plans down with it.
  const educationProjection = useMemo(() => {
    if (!catalog) return { plans: [], error: null };
    try {
      return { plans: buildEducationPricingPlans(catalog, EDUCATION_PLANS), error: null };
    } catch (error) {
      console.error('[PricingPage] Bootcamp pricing unavailable', error);
      return { plans: [], error };
    }
  }, [catalog]);
  const educationPlans = educationProjection.plans;
  const educationUnavailable = Boolean(educationProjection.error);

  const pricingExperiment = ACTIVE_EXPERIMENTS.PRICING_PAGE_REDESIGN_2026_07;
  const pricingDesignParam = queryParams.get('pricingDesign');
  const hasPricingDesignOverride =
    isPreviewExperimentOverrideHost() && PRICING_DESIGN_VARIANTS.includes(pricingDesignParam);
  const { variant: pricingFlagVariant, isLoading: pricingExperimentLoading } = useABTest(
    pricingExperiment.name,
    pricingExperiment.featureFlag,
    'control',
    {
      page: '/pricing',
      validVariants: PRICING_DESIGN_VARIANTS,
      normalizeControlExposure: true,
      skipFeatureFlagEvaluation: hasPricingDesignOverride,
      forcedVariant: hasPricingDesignOverride ? pricingDesignParam : undefined,
    },
  );
  const pricingDesignVariant = hasPricingDesignOverride ? pricingDesignParam : pricingFlagVariant;
  const isPricingRedesign = pricingDesignVariant === 'test';

  useEffect(() => {
    setBillingPeriod(requestedBillingPeriod);
  }, [requestedBillingPeriod]);

  useEffect(() => {
    if (!location.hash || pricingExperimentLoading) return undefined;

    const targetId = decodeURIComponent(location.hash.slice(1));
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(targetId);
      if (!target) return;

      const targetTop = window.scrollY + target.getBoundingClientRect().top - 96;
      window.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isPricingRedesign, location.hash, pricingExperimentLoading]);

  // NOTE: /pricing shows the currently active public discounted offer. The
  // Light-vs-Starter A/B test still runs only on generic sales/marketplace
  // /checkout/starter-plan entries with no explicit product selection.

  // Track pricing page view
  useEffect(() => {
    if (pricingExperimentLoading) return;

    trackEvent({
      event: 'PricingPageView',
      source: 'pricing_page',
      experiment_name: pricingExperiment.name,
      experiment_variant: pricingDesignVariant || 'control',
      feature_flag: pricingExperiment.featureFlag,
    });
  }, [
    pricingDesignVariant,
    pricingExperiment.featureFlag,
    pricingExperiment.name,
    pricingExperimentLoading,
    trackEvent,
  ]);

  const handleFeatureToggle = useCallback((featureKey) => {
    setActiveFeature((prev) => (prev === featureKey ? null : featureKey));
  }, []);

  const handleBillingPeriodChange = useCallback(
    (nextBillingPeriod) => {
      if (nextBillingPeriod === effectiveBillingPeriod) return;
      setPendingPlanId(null);
      setBillingPeriod(nextBillingPeriod);

      const nextQueryParams = new URLSearchParams(queryParams);
      if (nextBillingPeriod === 'annual') {
        nextQueryParams.set('billing', 'annual');
      } else {
        nextQueryParams.delete('billing');
      }
      navigate(
        {
          pathname: location.pathname,
          search: nextQueryParams.toString() ? `?${nextQueryParams.toString()}` : '',
          hash: location.hash,
        },
        { replace: true, state: location.state },
      );

      trackEvent({
        event: 'PricingBillingPeriodChanged',
        source: '/pricing',
        billing_period: nextBillingPeriod,
        experiment_name: pricingExperiment.name,
        experiment_variant: pricingDesignVariant || 'control',
        feature_flag: pricingExperiment.featureFlag,
      });
    },
    [
      effectiveBillingPeriod,
      location.hash,
      location.pathname,
      location.state,
      navigate,
      pricingDesignVariant,
      pricingExperiment.featureFlag,
      pricingExperiment.name,
      queryParams,
      trackEvent,
    ],
  );

  const handlePlanClick = useCallback(
    async (tier) => {
      // A closed Bootcamp enrollment keeps its card visible but cannot start checkout.
      if (!tier || pendingPlanId || tier.checkoutClosedMessage) return;

      setPendingPlanId(tier.id);
      await waitForButtonFeedbackPaint();

      // Determine destination for analytics
      const destination = tier.useCartCheckout ? '/checkout' : tier.ctaLink || '/checkout';

      // Track pricing CTA click - maps to PricingCTAClick in PostHog for A/B test metrics
      trackEvent({
        event: ANALYTICS_EVENTS.PRICING_CTA_CLICK,
        button_text: tier.cta,
        source: '/pricing',
        destination,
        plan: tier.id,
        price: tier.price,
        billing_period: tier.billingPeriod || effectiveBillingPeriod,
        plan_key: tier.planKey || null,
        user_type: isAuthenticated ? 'authenticated' : 'anonymous',
        experiment_name: pricingExperiment.name,
        experiment_variant: pricingDesignVariant || 'control',
        feature_flag: pricingExperiment.featureFlag,
      });

      if (!tier.isExternal && destination.startsWith('/checkout')) {
        trackEvent({
          event: ANALYTICS_EVENTS.SUBSCRIPTION_CTA_CLICKED,
          source: '/pricing',
          current_path: `${location.pathname}${location.search || ''}`,
          action: 'subscribe',
          feature: tier.id,
          plan: tier.id,
          product_slug: tier.productSlug || null,
          plan_key: tier.planKey || null,
          billing_period: tier.billingPeriod || effectiveBillingPeriod,
          destination,
        });
      }

      try {
        // Handle external links (like Calendly for Business plan)
        if (tier.isExternal) {
          window.open(tier.ctaLink, '_blank', 'noopener,noreferrer');
          setPendingPlanId(null);
          return;
        }

        // Handle cart-based checkout (Starter, Pro, All Access, Bootcamp)
        if (tier.useCartCheckout && tier.productSlug) {
          const origin = {
            flow: 'pricing_page',
            plan: tier.id,
            planKey: tier.planKey || null,
            billingPeriod: tier.billingPeriod || effectiveBillingPeriod,
            experimentName: pricingExperiment.name,
            experimentVariant: pricingDesignVariant || 'control',
            featureFlag: pricingExperiment.featureFlag,
          };
          if (tier.type === 'course') {
            // Bootcamp buyers land on the same welcome page the course pages
            // send them to (CoursePage / CourseSelfPacedPage).
            origin.returnUrl = '/ai-bot-builder-bootcamp/watch?welcome=1';
          }
          await startCheckout({
            products: [{ productSlug: tier.productSlug, planKey: tier.planKey }],
            flowContext: 'pricing_page',
            metadata: { origin },
            contact: {
              email: currentUser?.email || '',
              name: currentUser?.name || '',
            },
          });
          return;
        }

        // Handle direct link navigation (fallback)
        if (tier.ctaLink) {
          await waitForDirectCheckoutButtonFeedback();
          navigateToCheckoutOverlay(navigate, tier.ctaLink, location);
        } else {
          setPendingPlanId(null);
        }
      } catch (error) {
        setPendingPlanId(null);
        throw error;
      }
    },
    [
      isAuthenticated,
      effectiveBillingPeriod,
      currentUser?.email,
      currentUser?.name,
      location,
      navigate,
      pendingPlanId,
      pricingDesignVariant,
      pricingExperiment.featureFlag,
      pricingExperiment.name,
      startCheckout,
      trackEvent,
    ],
  );

  if (catalogLoading === true || pricingExperimentLoading) {
    return (
      <div className="pricing-page pricing-page--catalog-state" role="status">
        <h1>Plans and pricing</h1>
        <p>
          <Spinner animation="border" size="sm" aria-hidden="true" /> Loading current pricing.
        </p>
      </div>
    );
  }

  if (catalogLoading === false && (catalogError || !catalog || pricingCatalogProjection.error)) {
    return (
      <div className="pricing-page pricing-page--catalog-state" role="alert">
        <h1>Plans and pricing</h1>
        <p>Current pricing is unavailable. Please refresh and try again.</p>
      </div>
    );
  }

  if (isPricingRedesign) {
    return (
      <PricingPageExperiment
        businessPlan={BUSINESS_PLAN}
        corePlans={CORE_PLANS}
        billingPeriod={effectiveBillingPeriod}
        billingPeriodControl={
          annualBillingAvailable ? (
            <BillingPeriodToggle
              billingPeriod={effectiveBillingPeriod}
              onChange={handleBillingPeriodChange}
            />
          ) : null
        }
        educationPlans={educationPlans}
        educationUnavailable={educationUnavailable}
        formatPrice={formatPrice}
        onPlanClick={handlePlanClick}
        pendingPlanId={pendingPlanId}
      />
    );
  }

  return (
    <div className="pricing-page">
      <SiteRouteHelmet path="/pricing" />
      {/* The crawler snapshot is taken only from the live checkout catalog,
      never from the tier defaults this page renders before it loads. */}
      <PrerenderReady ready={Boolean(catalog)} state="catalog" />

      {/* Animated background hazes */}
      <div className="pricing-page-background" />
      <div className="pricing-page-effects" aria-hidden="true">
        <div className="pricing-page-haze pricing-page-haze--purple" />
        <div className="pricing-page-haze pricing-page-haze--cyan" />
        <div className="pricing-page-haze pricing-page-haze--amber" />
      </div>

      {/* Hero Section */}
      <section className="pricing-page-hero">
        <div className="container">
          <h1 className="pricing-page-title">Choose Your Trading Power</h1>
          <p className="pricing-page-subtitle">
            Learn it live in the AI Bootcamp, or run BotSpot on your own with a monthly plan.
          </p>
          <p className="pricing-page-jump">
            <a href="#pricing-training">AI Bootcamp</a>
            <span aria-hidden="true">·</span>
            <a href="#pricing-plans">Monthly plans</a>
          </p>
        </div>
      </section>

      {/* AI Bootcamp leads the page: it is the highest-value purchase per buyer
      (Rob, 2026-10-14). Prices come only from the checkout catalog. */}
      <section className="pricing-page-secondary pricing-page-training" id="pricing-training">
        <div className="container">
          <div className="pricing-page-section-header">
            <h2 className="pricing-page-section-title">AI Bootcamp</h2>
            <p className="pricing-page-section-subtitle">
              A one-time purchase, separate from your monthly plan. Learn live with the class, or
              start the recordings today.
            </p>
          </div>

          {educationUnavailable ? (
            <p className="pricing-page-section-subtitle" role="alert">
              Bootcamp pricing is unavailable right now. Please refresh and try again.
            </p>
          ) : (
            <div className="pricing-page-secondary-grid">
              {educationPlans.map((educationPlan) => (
                <div
                  key={educationPlan.id}
                  className="pricing-page-card pricing-page-card--addon pricing-page-card--purple"
                  id={`pricing-plan-${educationPlan.id}`}
                >
                  <div className="pricing-addon-badge">
                    {educationPlan.id === 'bootcamp-live' ? 'Live class' : 'Start today'}
                  </div>
                  <div className="pricing-page-card-header">
                    <h3 className="pricing-page-card-name">{educationPlan.name}</h3>
                    <div className="pricing-page-price-container">
                      {educationPlan.originalPrice && (
                        <span className="pricing-page-price-original">
                          {formatPrice(educationPlan.originalPrice)}
                        </span>
                      )}
                      <span className="pricing-page-price" data-plan-price="true">
                        {formatPrice(educationPlan.price)}
                      </span>
                      {educationPlan.discountPercent > 0 && (
                        <span className="pricing-page-discount-badge">
                          {educationPlan.discountPercent}% OFF
                        </span>
                      )}
                      <span className="pricing-page-price-period">{educationPlan.priceSuffix}</span>
                      {educationPlan.priceNote && (
                        <span className="pricing-page-price-equivalent">
                          {educationPlan.priceNote}
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="pricing-page-card-tagline">{educationPlan.tagline}</p>
                  {educationPlan.bestFor && (
                    <p className="pricing-page-card-bestfor">Best for: {educationPlan.bestFor}</p>
                  )}

                  <ul className="pricing-page-features pricing-page-features--compact">
                    {[
                      ...educationPlan.facts.map((fact) => ({ label: fact })),
                      ...educationPlan.features,
                    ].map((feature, index) => (
                      <PricingFeatureItem
                        activeFeature={activeFeature}
                        feature={feature}
                        index={index}
                        key={`${educationPlan.id}-${index}`}
                        onToggleFeature={handleFeatureToggle}
                        planId={educationPlan.id}
                      />
                    ))}
                  </ul>

                  {educationPlan.checkoutClosedMessage && (
                    <p className="pricing-page-section-subtitle" role="status">
                      {educationPlan.checkoutClosedMessage}
                    </p>
                  )}
                  <button
                    className="pricing-page-cta pricing-page-cta--purple"
                    onClick={() => handlePlanClick(educationPlan)}
                    disabled={
                      Boolean(pendingPlanId) || Boolean(educationPlan.checkoutClosedMessage)
                    }
                    aria-busy={pendingPlanId === educationPlan.id}
                  >
                    {pendingPlanId === educationPlan.id ? (
                      <PricingButtonLoadingLabel />
                    ) : (
                      educationPlan.cta
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Cards Grid */}
      <section className="pricing-page-cards" id="pricing-plans">
        <div className="container">
          <div className="pricing-page-section-header">
            <h2 className="pricing-page-section-title">Monthly plans</h2>
            <p className="pricing-page-section-subtitle">
              Use BotSpot on your own. Every plan includes the full workflow.
            </p>
          </div>
          {annualBillingAvailable && (
            <div className="pricing-page-cards-toggle">
              <BillingPeriodToggle
                billingPeriod={effectiveBillingPeriod}
                onChange={handleBillingPeriodChange}
              />
            </div>
          )}
          <div className="pricing-page-grid pricing-page-core-grid">
            {CORE_PLANS.map((tier) => (
              <div
                key={tier.id}
                id={`pricing-plan-${tier.id}`}
                className={`pricing-page-card pricing-grid-${tier.id} ${tier.featured ? 'pricing-page-card--featured' : ''} pricing-page-card--${tier.theme}`}
              >
                {tier.featured && <div className="pricing-page-badge">Most Popular</div>}

                <div className="pricing-page-card-header">
                  <h3 className="pricing-page-card-name">{tier.name}</h3>
                  {/* Urgency message for discount variant */}
                  {tier.urgencyMessage && (
                    <div className="pricing-page-urgency-badge">{tier.urgencyMessage}</div>
                  )}
                  <div className="pricing-page-price-container">
                    {/* Show strikethrough original price for discount variant */}
                    {tier.originalPrice && tier.billingPeriod !== 'annual' && (
                      <span className="pricing-page-price-original">${tier.originalPrice}</span>
                    )}
                    <span
                      className="pricing-page-price"
                      data-plan-price={tier.priceLabel ? undefined : 'true'}
                    >
                      {tier.priceLabel ? tier.priceLabel : formatPrice(tier.price)}
                    </span>
                    {/* Discount percentage badge */}
                    {tier.discountPercent && tier.billingPeriod !== 'annual' && (
                      <span className="pricing-page-discount-badge">
                        {tier.discountPercent}% OFF
                      </span>
                    )}
                    {!tier.priceLabel && tier.type === 'subscription' && (
                      <span className="pricing-page-price-period">
                        per {tier.pricePeriod || 'month'}
                      </span>
                    )}
                    {!tier.priceLabel && tier.type === 'course' && (
                      <span className="pricing-page-price-period">one-time</span>
                    )}
                    {tier.billingPeriod === 'annual' && tier.monthlyEquivalentPrice && (
                      <span className="pricing-page-price-equivalent">
                        {formatPrice(tier.monthlyEquivalentPrice)}/month · 2 months free
                      </span>
                    )}
                  </div>
                </div>

                <p className="pricing-page-card-tagline">{tier.tagline}</p>
                {tier.bestFor && (
                  <p className="pricing-page-card-bestfor">Best for: {tier.bestFor}</p>
                )}

                <ul className="pricing-page-features pricing-page-features--compact">
                  {tier.features.map((feature, index) => (
                    <PricingFeatureItem
                      activeFeature={activeFeature}
                      feature={feature}
                      index={index}
                      key={`${tier.id}-${index}`}
                      onToggleFeature={handleFeatureToggle}
                      planId={tier.id}
                    />
                  ))}
                </ul>
                <PricingMobileFeatureDisclosure features={tier.features} />

                <button
                  className={`pricing-page-cta ${tier.featured ? 'pricing-page-cta--featured' : ''}`}
                  onClick={() => handlePlanClick(tier)}
                  disabled={Boolean(pendingPlanId)}
                  aria-busy={pendingPlanId === tier.id}
                >
                  {pendingPlanId === tier.id ? <PricingButtonLoadingLabel /> : tier.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise */}
      <section
        className="pricing-page-secondary pricing-page-secondary--enterprise"
        id="pricing-enterprise"
      >
        <div className="container">
          <div className="pricing-page-section-header">
            <h2 className="pricing-page-section-title">Enterprise</h2>
            <p className="pricing-page-section-subtitle">
              Custom plans for teams that need more capacity, support, or integrations.
            </p>
          </div>
          <div className="pricing-page-secondary-grid pricing-page-secondary-grid--single">
            {BUSINESS_PLAN && (
              <div
                className="pricing-page-card pricing-page-card--enterprise pricing-page-card--purple"
                id="pricing-plan-business"
              >
                <div className="pricing-addon-badge">Enterprise</div>
                <div className="pricing-page-card-header">
                  <h3 className="pricing-page-card-name">{BUSINESS_PLAN.name}</h3>
                  <div className="pricing-page-price-container">
                    <span className="pricing-page-price">
                      {BUSINESS_PLAN.priceLabel || 'Custom'}
                    </span>
                  </div>
                </div>

                <p className="pricing-page-card-tagline">{BUSINESS_PLAN.tagline}</p>
                {BUSINESS_PLAN.bestFor && (
                  <p className="pricing-page-card-bestfor">Best for: {BUSINESS_PLAN.bestFor}</p>
                )}

                <ul className="pricing-page-features pricing-page-features--compact">
                  {(BUSINESS_PLAN.features || []).map((feature, index) => (
                    <PricingFeatureItem
                      activeFeature={activeFeature}
                      feature={feature}
                      index={index}
                      key={`business-${index}`}
                      onToggleFeature={handleFeatureToggle}
                      planId="business"
                    />
                  ))}
                </ul>
                <PricingMobileFeatureDisclosure features={BUSINESS_PLAN.features || []} />

                <button
                  className="pricing-page-cta"
                  onClick={() => handlePlanClick(BUSINESS_PLAN)}
                  disabled={Boolean(pendingPlanId)}
                  aria-busy={pendingPlanId === BUSINESS_PLAN.id}
                >
                  {pendingPlanId === BUSINESS_PLAN.id ? (
                    <PricingButtonLoadingLabel />
                  ) : (
                    BUSINESS_PLAN.cta
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Compare plans section removed as requested */}

      {/* Guarantee Section */}
      <section className="pricing-page-guarantee">
        <div className="container">
          <div className="pricing-page-guarantee-card">
            <div className="pricing-page-guarantee-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12l2 2 4-4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="pricing-page-guarantee-content">
              <h3>Flexible subscriptions</h3>
              <p>
                Choose monthly or annual billing and manage your subscription from your account.
              </p>
            </div>
          </div>

          <TrustStrip className="pricing-trust-strip" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="pricing-page-final-cta">
        <div className="container">
          <h2>Ready to automate your trading?</h2>
          <p>Join thousands of traders already using BotSpot to build winning strategies</p>
          <Link to="/sales" className="pricing-page-final-cta-btn">
            Get Started Free
          </Link>
        </div>
      </section>
    </div>
  );
}
